import React, { useState, useEffect, useRef } from 'react';

/**
 * Selecao unica com busca digitando.
 *
 * Enquanto nada esta escolhido, mostra um campo de busca com os resultados
 * abaixo. Escolhido, vira uma etiqueta com botao de limpar — assim nunca fica
 * ambiguo se o texto no campo e o que esta selecionado ou o que se digitou.
 */
export default function SearchSelect({
  value,
  onChange,
  fetchOptions,
  placeholder = 'Digite para buscar...',
  emptyMessage = 'Nenhum resultado.',
  allLabel = 'Todos',
}) {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  // Debounce: uma requisicao por pausa na digitacao, nao por tecla
  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const timer = setTimeout(async () => {
      try { setOptions(await fetchOptions(search)); } catch { setOptions([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [search, open]); // eslint-disable-line

  // Clicar fora fecha a lista, senao ela fica sobre o resto da tela
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function escolher(option) {
    onChange(option);
    setSearch('');
    setOpen(false);
  }

  if (value) {
    return (
      <div className="flex items-center gap-2 h-[38px] px-3 border rounded-md bg-surface-inset">
        <span className="flex-1 min-w-0 text-sm truncate" title={value.label}>{value.label}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          title="Limpar"
          className="text-ink-muted hover:text-ink-primary text-lg leading-none flex-shrink-0"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <input
        className="form-input"
        placeholder={placeholder}
        value={search}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
      />

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-surface-100 border rounded-md shadow-lg max-h-[240px] overflow-y-auto">
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm text-ink-muted hover:bg-surface-inset border-b border-subtle"
            onClick={() => escolher(null)}
          >
            {allLabel}
          </button>

          {loading && <div className="px-3 py-2 text-sm text-ink-muted">Buscando...</div>}

          {!loading && options.length === 0 && (
            <div className="px-3 py-2 text-sm text-ink-muted">{emptyMessage}</div>
          )}

          {!loading && options.map((o) => (
            <button
              key={o.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-surface-inset"
              onClick={() => escolher(o)}
            >
              <span className="block text-sm truncate">{o.label}</span>
              {o.sublabel && <span className="block text-xs text-ink-muted truncate">{o.sublabel}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
